import React from "react";

import {
  BackfillQueueResponse,
  SnapshotQueueResponse,
} from "../../../../../services";
import { Caption, Card, CardHeader, CardTitle } from "../../../styles";
import { formatAgeHours } from "../../../utils/format";
import { SECTION_LABELS } from "../../constants";
import { QueueBar } from "./QueueBar";
import { QueueGroup } from "./QueueGroup";
import { BackgroundJobsProps } from "./types";

const INFO = "var(--color-status-info)";
const WARNING = "var(--color-status-warning)";
const ERROR = "var(--color-status-error)";
const SUCCESS = "var(--color-status-success)";
const MUTED = "var(--color-gray-500)";

const snapshotSegments = (queue: SnapshotQueueResponse) => [
  { label: "Pending", count: queue.pendingJobs, color: INFO },
  { label: "Processing", count: queue.processingJobs, color: SUCCESS },
  {
    label: "Coverage-blocked",
    count: queue.coverageBlockedJobs - queue.preHorizonCoverageBlockedJobs,
    color: WARNING,
  },
  {
    label: "Pre-horizon blocked",
    count: queue.preHorizonCoverageBlockedJobs,
    color: MUTED,
  },
  { label: "Failed", count: queue.failedJobs, color: ERROR },
];

const backfillSegments = (queue: BackfillQueueResponse) => [
  { label: "Queued", count: queue.queuedJobs, color: INFO },
  { label: "Running", count: queue.runningJobs, color: SUCCESS },
  { label: "Finalizing", count: queue.finalizingJobs, color: MUTED },
  { label: "Rate-limited", count: queue.rateLimitedJobs, color: WARNING },
  {
    label: "Partial",
    count: queue.partialJobs,
    color: "var(--color-warning-4)",
  },
  { label: "Exhausted", count: queue.exhaustedJobs, color: ERROR },
];

const oldestPart = (label: string, job: { ageHours: number } | null) =>
  job ? [`${label} ${formatAgeHours(job.ageHours)}`] : [];

const snapshotNote = (queue: SnapshotQueueResponse) =>
  [
    `${queue.completedToday} completed today`,
    ...oldestPart("Oldest pending", queue.oldestPendingJob),
    ...oldestPart("Oldest blocked", queue.oldestCoverageBlockedJob),
  ].join(" · ");

const backfillNote = (queue: BackfillQueueResponse) =>
  [
    ...oldestPart("Oldest queued", queue.oldestQueuedJob),
    ...oldestPart("Oldest rate-limited", queue.oldestRateLimitedJob),
    queue.oldestStaleLease
      ? `Stale lease ${formatAgeHours(queue.oldestStaleLease.ageHours)}`
      : "No stale leases",
  ].join(" · ");

export const BackgroundJobs: React.FC<BackgroundJobsProps> = ({
  snapshotQueue,
  backfillQueue,
  onRetrySnapshotQueue,
  onRetryBackfillQueue,
}) => (
  <Card aria-label="Background jobs" data-testid="admin-background-jobs">
    <CardHeader>
      <CardTitle>Background jobs</CardTitle>
    </CardHeader>
    <QueueGroup
      title={SECTION_LABELS.snapshotQueue}
      section={snapshotQueue}
      onRetry={onRetrySnapshotQueue}
      testId="admin-snapshot-queue"
    >
      {(queue) => (
        <>
          <QueueBar
            segments={snapshotSegments(queue)}
            emptyLabel="No snapshot jobs in the queue"
          />
          <Caption>{snapshotNote(queue)}</Caption>
        </>
      )}
    </QueueGroup>
    <QueueGroup
      title={SECTION_LABELS.backfillQueue}
      section={backfillQueue}
      onRetry={onRetryBackfillQueue}
      testId="admin-backfill-queue"
    >
      {(queue) => (
        <>
          <QueueBar
            segments={backfillSegments(queue)}
            emptyLabel="No backfill jobs in the queue"
          />
          <Caption>{backfillNote(queue)}</Caption>
        </>
      )}
    </QueueGroup>
  </Card>
);
